import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Check, Hotel, ArrowRight, ShieldCheck, FileText } from 'lucide-react';

/**
 * Pricing-Karte "Hotel-Reservierung für Visa" neben der Alerts-Karte.
 * Eingeloggte Nutzer landen direkt im Bestellbereich des Dashboards,
 * alle anderen zuerst bei der Registrierung.
 */
const HotelPricingCard = () => {
  const { t } = useLanguage();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const features = [
    t('hotel_feature_1'),
    t('hotel_feature_2'),
    t('hotel_feature_3'),
    t('hotel_feature_4'),
    t('hotel_feature_5'),
  ];

  const handleOrder = () => {
    navigate(isAuthenticated ? '/dashboard#hotel-booking' : '/register');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: 0.1 }}
      className="h-full"
    >
      <Card className="bg-white rounded-sm overflow-hidden relative border-2 border-[#FF3B30] flex flex-col" data-testid="hotel-pricing-card">
        <div className="bg-gradient-to-r from-[#FF3B30] to-[#FF6B5B] text-white text-center py-3 px-4">
          <div className="flex items-center justify-center gap-2">
            <span className="animate-pulse">✨</span>
            <span className="font-bold text-sm">{t('hotel_ribbon')}</span>
            <span className="animate-pulse">✨</span>
          </div>
          <p className="text-xs text-white/90 mt-1">{t('hotel_subtitle')}</p>
        </div>

        <CardContent className="p-8 flex flex-col">
          <div className="text-center pb-6 border-b border-gray-100">
            <div className="w-16 h-16 bg-[#FF3B30]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Hotel className="w-8 h-8 text-[#FF3B30]" />
            </div>
            <h3 className="font-heading font-bold text-xl text-[#0A0A0A] mb-1">{t('hotel_title')}</h3>
            <p className="text-gray-500 text-sm">{t('hotel_tagline')}</p>
          </div>

          <div className="text-center py-6">
            <div className="flex items-baseline justify-center gap-1">
              <span className="text-lg text-[#FF3B30]">€</span>
              <span className="text-6xl font-heading font-bold text-[#FF3B30]">{t('hotel_price')}</span>
              <span className="text-gray-500 ml-1">{t('hotel_period')}</span>
            </div>
            <p className="text-sm text-gray-400 mt-2">{t('hotel_price_note')}</p>

            {/* Tunesien-Preis: gleiche "Karte in der Karte" wie beim Alerts-Abo */}
            <div className="mt-5 mx-auto max-w-xs overflow-hidden rounded-2xl border border-[#FF3B30]/15 bg-white shadow-sm shadow-[#FF3B30]/5">
              <div className="flex items-center justify-between gap-2 bg-gradient-to-r from-[#FF3B30] to-[#FF6B5B] px-4 py-2">
                <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-white">
                  <span className="text-sm leading-none">🇹🇳</span>
                  {t('pricing_tnd_exclusive')}
                </span>
              </div>
              <div className="flex items-center justify-between gap-3 px-4 py-3">
                <span className="text-2xl font-heading font-bold text-[#0A0A0A]">
                  {t('hotel_price_tnd')} <span className="text-sm font-semibold text-gray-500">TND</span>
                </span>
                <div className="flex items-center gap-1 text-[11px] text-gray-400">
                  <ShieldCheck className="w-3.5 h-3.5 text-green-500" />
                  DHMAD.tn
                </div>
              </div>
            </div>
          </div>

          <ul className="space-y-3 mb-6">
            {features.map((feature) => (
              <li key={feature} className="flex items-center gap-3 p-2 rounded-sm hover:bg-gray-50 transition-colors">
                <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-white" />
                </div>
                <span className="text-[#0A0A0A] font-medium">{feature}</span>
              </li>
            ))}
          </ul>

          {/* Zwei Zahlungsoptionen wie beim Alerts-Abo; die Bestellung selbst
              (Reisende, Daten) läuft im Dashboard, daher führen beide dorthin. */}
          <div className="grid grid-cols-1 gap-3 mt-auto">
            <Button
              className="bg-[#FF3B30] hover:bg-[#E5352B] text-white font-bold h-14 rounded-sm text-base shadow-lg shadow-[#FF3B30]/25 transition-all hover:shadow-xl hover:shadow-[#FF3B30]/30"
              onClick={handleOrder}
              data-testid="hotel-pricing-cta"
            >
              {t('hotel_buy_stripe')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              variant="outline"
              className="border-2 border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 font-bold h-14 rounded-sm text-base"
              onClick={handleOrder}
              data-testid="hotel-pricing-dhmad"
            >
              🇹🇳 {t('hotel_pay_dhmad')} ({t('hotel_price_tnd')} TND)
            </Button>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-100 flex items-center justify-center gap-4 text-xs text-gray-400">
            <div className="flex items-center gap-1">
              <ShieldCheck className="w-4 h-4" />
              <span>Secure Payment</span>
            </div>
            <div className="flex items-center gap-1">
              <FileText className="w-4 h-4" />
              <span>PDF</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default HotelPricingCard;
